import { changeElementHorizonAlign, changeElementVerticalAlign, resizeElement } from '../canvas/actions';
import { colors, elementStatus, elementType, hMultiAlign, horizonAlign, undoRedoType } from '../utils/constants';
import {
  getRealPosToKeepElementInside,
  horizonAlignElement,
  verticalAlignElement,
} from './components/BaseElement/logics';
import { getRectPoints, rotateText } from '../canvas/logics';

import axios from 'axios';
import { getBatchSelectedTextList } from '../canvas/selectors';
import { getCanvasDraggingArea } from '../canvas/selectors';
import { getColorChangeElements } from './selectors';
import { getCurrentElement } from './selectors';
import { getCurrentImprintAreaKey } from '../main/selectors';
import { getDisplaySize } from './utils';
import { getElementSizeByFontSize } from '../text/logics';
import { getFontSizeRange } from '../text/selectors';
import { getRotatedElementRange } from './utils';
import { getSingleSelectedElement } from '../nextgen/main/MainPanel/selectors';
import { getStudioItemType } from '../main/selectors';
import { getTextImage as getTextImageApi } from './apis';
import { pushToUndoList } from '../canvas/components/UndoRedo/logic';
import { studioItemType as studioItemTypeEnum } from '../main/constants';
import { v4 as uuidv4 } from 'uuid';

/** create text element
 * @param  {Object} additionalTextInfo - additional text info we need to create a text element
 * @param  {number} additionalTextInfo.minFontSize - min font size
 * @param  {number} additionalTextInfo.maxFontSize - max font size
 * @param  {string} additionalTextInfo.fontFace - e.g. 'Arial'
 * @param  {string} additionalTextInfo.fontFileName - e.g. 'Arial.ttf'
 * @param  {string} additionalTextInfo.fontImgFileName - e.g. 'Arial.png'
 * @param  {number} additionalTextInfo.zIndex - initial zindex of the element
 * @param  {string} text
 * @returns {Object} element object
 */
export const createTextElement = function (additionalTextInfo, isNextgenUi) {
  const textElement = {};
  textElement.ui = {};
  textElement.data = {};
  textElement.ui.guid = uuidv4();
  textElement.ui.status = elementStatus.SingleSelected;
  textElement.ui.maxWidth = 0; //width/height should be set according to the reponse data from the server
  textElement.ui.maxHeight = 0;
  textElement.ui.minWidth = 0; //width/height should be set according to the reponse data from the server
  textElement.ui.minHeight = 0;
  textElement.ui.fontFileName = additionalTextInfo.fontFileName;
  textElement.ui.fontImgFileName = additionalTextInfo.fontImgFileName;
  textElement.ui.maxFontSize = additionalTextInfo.maxFontSize;
  textElement.ui.visible = false;
  textElement.ui.outlineRect = { x: 80, y: 80, width: 0, height: 0 };

  textElement.data.x = 80;
  textElement.data.y = 80;
  textElement.data.itemElementId = -1; //hardcode to -1 for custom element
  textElement.data.fontFace = additionalTextInfo.fontFace;
  textElement.data.fontCode = additionalTextInfo.fontCode;
  textElement.data.color = additionalTextInfo.color;
  textElement.data.direction = 0;
  textElement.data.imprintText = isNextgenUi ? '' : additionalTextInfo.text;
  textElement.data.defaultText = isNextgenUi ? additionalTextInfo.text : 'Custom Text';
  textElement.data.fontSize = additionalTextInfo.fontSize;
  textElement.data.isBold = additionalTextInfo.isBold;
  textElement.data.isItalic = additionalTextInfo.isItalic;
  textElement.data.isUnderline = false;
  textElement.data.horizonAlign = horizonAlign.Left;
  textElement.data.horizonMultiAlign = hMultiAlign.Left;
  textElement.data.outlineThickness = additionalTextInfo.outlineThickness;
  textElement.data.outlineColor = additionalTextInfo.outlineColor;
  textElement.data.textMode = additionalTextInfo.textMode;
  textElement.data.width = 0; //to be set according by font size
  textElement.data.height = 0; //to be set according by font size
  textElement.data.onBack = 1;
  textElement.data.customElement = true;
  textElement.data.zIndex = additionalTextInfo.zIndex; //keep the new element on top of the others

  return textElement;
};
/** get text image
 * @param  {number} maxFontSize - max fontsize allowed
 * @param  {string} text - imprint text
 * @param  {string} family - font family file name, e.g. Arial.ttf
 * @param  {boolean} isBold - if bold font
 * @param  {boolean} isItalic - if italic font
 * @param  {boolean} isUnderline - if has underline
 * @param  {string} rgb - 6-digits rgb color without '#'
 * @param  {number} horizonMultiAlign - horizontal align type(element inner alignment)
 * @param  {float} studioScale - studio scale
 * @param  {int} vendorId - vendor id
 * @param  {int} scaleFactor=1 - scale factor
 * @returns {Promise} text image promise object
 */

//let cancelTokenSource = null;
let textCancelTokenSource = {};
export const getTextImage = function (
  maxFontSize,
  text,
  fontFace,
  family,
  isBold,
  isItalic,
  isUnderline,
  rgb, //without #
  horizonMultiAlign,
  outlineThickness,
  outlineColorReference,
  studioScale,
  vendorId,
  useColorImage = false,
  colorImageUrl = '',
  useDeboss = false,
  scaleFactor = 1,
  textGuid = null,
  canBeCancelled = false
) {
  let cancelTokenSource = null;

  if (textGuid) {
    if (Object.keys(textCancelTokenSource).includes(textGuid)) {
      cancelTokenSource = textCancelTokenSource[textGuid];
      if (cancelTokenSource) {
        cancelTokenSource.cancel();
      }
    }

    if (canBeCancelled) {
      cancelTokenSource = axios.CancelToken.source();
      textCancelTokenSource[textGuid] = cancelTokenSource;
    }
  }

  const queries = `?MaxFontSize=${maxFontSize}&Text=${encodeURIComponent(text)}&FontFace=${encodeURIComponent(
    fontFace
  )}&Family=${encodeURIComponent(
    family
  )}&Bold=${isBold}&Italic=${isItalic}&Underline=${isUnderline}&Color=${encodeURIComponent(
    rgb
  )}&HMultiAlign=${horizonMultiAlign}&OutlineThickness=${outlineThickness}&OutlineColor=${encodeURIComponent(
    outlineColorReference
  )}&StudioScale=${studioScale}&ScaleFactor=${scaleFactor}&VendorId=${vendorId}&useColorImage=${useColorImage}&colorImageUrl=${colorImageUrl}&useDeboss=${useDeboss}`;

  const config = canBeCancelled ? { cancelToken: cancelTokenSource.token } : {};
  return getTextImageApi(queries, config)
    .then((res) => {
      if (textGuid && Object.keys(textCancelTokenSource).includes(textGuid)) {
        textCancelTokenSource[textGuid] = null;
      }

      return res;
    })
    .catch((error) => {
      if (!axios.isCancel(error)) {
        console.error(error);
      }
    });
};

/** check if the element fits the canvas and calendar sides
 * @param  {Object} draggingArea - dragging area
 * @param  {Object} elementSize - element real size {width,height}
 * @param  {number} direction - direction
 * @param  {enum} type - elementType
 * @returns check results: e.g. { canvasFit: true, sideFit: true }
 */
export const checkElementFitness = function (draggingArea, elementSize, direction, type = elementType.Text) {
  const displaySize = getDisplaySize(elementSize.width, elementSize.height, direction);
  const { displayWidth, displayHeight } = displaySize;
  const result = { canvasFit: true, sideFit: true };
  if (displayWidth > draggingArea.width || displayHeight > draggingArea.height) {
    result.canvasFit = false;
  }
  if (draggingArea.isCalendar && type === elementType.Text) {
    if (displayWidth > draggingArea.sideWidth) {
      result.sideFit = false;
    }
  }
  return result;
};

export const checkTextElementFitness = function (draggingArea, elementSize, type = elementType.Text) {
  const result = { canvasFit: true, sideFit: true };
  if (elementSize.width > draggingArea.width || elementSize.height > draggingArea.height) {
    result.canvasFit = false;
  }
  if (draggingArea.isCalendar && type === elementType.Text) {
    if (elementSize.width > draggingArea.sideWidth) {
      result.sideFit = false;
    }
  }
  return result;
};

export const checkNextGenTextElementFitness = function (draggingArea, newRotatedPoints, type = elementType.Text) {
  const result = { canvasFit: true, sideFit: true };

  const { minX, minY, maxX, maxY } = getRotatedElementRange(newRotatedPoints);
  const maxWidth = maxX - minX;
  const maxHeight = maxY - minY;
  const elementSize = { width: maxWidth, height: maxHeight };
  if (elementSize.width > draggingArea.width || elementSize.height > draggingArea.height) {
    result.canvasFit = false;
  }
  if (draggingArea.isCalendar && type === elementType.Text) {
    if (elementSize.width > draggingArea.sideWidth) {
      result.sideFit = false;
    }
  }
  return result;
};

export const changeCanvasHorizonAlign = function (horizonAlignType) {
  return (dispatch, getState) => {
    const state = getState();
    let draggingArea = getCanvasDraggingArea(state);
    let imprintAreaKey = getCurrentImprintAreaKey(state);
    const textList = state.textDict[imprintAreaKey] || [];
    const imageList = state.imageDict[imprintAreaKey] || [];
    const elementList = textList.concat(imageList);
    let isText = textList.some(
      (text) => text.ui.status === elementStatus.SingleSelected || text.ui.status === elementStatus.BatchSelected
    );
    if (elementList && elementList.length > 0) {
      // get all selected text elements
      const selectedElementList = elementList.filter(
        (element) =>
          element.ui.status === elementStatus.SingleSelected || element.ui.status === elementStatus.BatchSelected
      );

      if (selectedElementList && selectedElementList.length > 0) {
        // get real dimension list
        selectedElementList.forEach((element) => {
          const alignedX = horizonAlignElement(
            draggingArea,
            { x: element.data.x, y: element.data.y },
            { width: element.data.width, height: element.data.height },
            element.data.direction,
            horizonAlignType,
            isText
          );
          dispatch(changeElementHorizonAlign({ imprintAreaKey, x: alignedX, guid: element.ui.guid }));
        });
        dispatch(pushToUndoList(undoRedoType.ChangeTemplateOrEditElement));
      }
    }
  };
};

export const changeCanvasVerticalAlign = function (verticalAlignType) {
  return (dispatch, getState) => {
    const state = getState();
    let draggingArea = getCanvasDraggingArea(state);
    let imprintAreaKey = getCurrentImprintAreaKey(state);
    const textList = state.textDict[imprintAreaKey] || [];
    const imageList = state.imageDict[imprintAreaKey] || [];
    const elementList = textList.concat(imageList);
    let isText = textList.some(
      (text) => text.ui.status === elementStatus.SingleSelected || text.ui.status === elementStatus.BatchSelected
    );
    if (elementList && elementList.length > 0) {
      // get all selected text elements
      const selectedElementList = elementList.filter(
        (element) =>
          element.ui.status === elementStatus.SingleSelected || element.ui.status === elementStatus.BatchSelected
      );

      if (selectedElementList && selectedElementList.length > 0) {
        // get real dimension list
        selectedElementList.forEach((element) => {
          const alignedY = verticalAlignElement(
            draggingArea,
            { x: element.data.x, y: element.data.y },
            { width: element.data.width, height: element.data.height },
            element.data.direction,
            verticalAlignType,
            isText
          );
          dispatch(changeElementVerticalAlign({ imprintAreaKey, y: alignedY, guid: element.ui.guid }));
        });
        dispatch(pushToUndoList(undoRedoType.ChangeTemplateOrEditElement));
      }
    }
  };
};

export const getElementList = function (elementDict, imprintAreaKey) {
  // initialize textElementList to [] if it doesn't exist
  if (!elementDict[imprintAreaKey]) {
    elementDict[imprintAreaKey] = [];
  }
  return elementDict[imprintAreaKey];
};
export const getElement = function (elementDict, imprintAreaKey, guid) {
  const elementList = getElementList(elementDict, imprintAreaKey);
  return elementList.find((e) => e.ui.guid === guid);
};

/** For TextElement Only
 * @param  {} elementPos
 * @param  {} elementSize
 */
export const resizeTextElementEnd = function (noRotateRect) {
  return (dispatch, getState) => {
    const state = getState();
    const imprintAreaKey = getCurrentImprintAreaKey(state);
    const currentElement = getCurrentElement(state);
    const fontSizeRange = getFontSizeRange(state);
    let draggingArea = getCanvasDraggingArea(state);
    const guid = currentElement.ui.guid;
    const degree = Math.max(currentElement.data.direction, 0);
    let newWidth = noRotateRect.width;
    let newHeight = noRotateRect.height;
    const maxWidth = currentElement.ui.maxWidth;
    const maxHeight = currentElement.ui.maxHeight;
    const maxFontSize = currentElement.ui.maxFontSize;
    // use the larger one to get the scale so to reduce the deviation
    let scale = newWidth > newHeight ? newWidth / maxWidth : newHeight / maxHeight;
    scale = Math.min(scale, 1);
    newWidth = maxWidth * scale;
    newHeight = maxHeight * scale;
    let newFontSize = Math.round(maxFontSize * scale);
    //use the same logic as the changeFontSize so to avoid different width/height with the same fontsize
    let elementSizeByFontSize = getElementSizeByFontSize(maxFontSize, maxWidth, maxHeight, newFontSize);

    const checkResult = checkTextElementFitness(
      draggingArea,
      elementSizeByFontSize,
      currentElement.data.direction,
      elementType.Text
    );
    if ((!checkResult.canvasFit || !checkResult.sideFit) && newFontSize > fontSizeRange.minFontSize) {
      // decrease the fontsize by 1 so that the element won't be too large to fit the canvas
      newFontSize--;
      elementSizeByFontSize = getElementSizeByFontSize(maxFontSize, maxWidth, maxHeight, newFontSize);
    }

    noRotateRect.width = elementSizeByFontSize.width;
    noRotateRect.height = elementSizeByFontSize.height;

    const realPos = getRealPosToKeepElementInside(
      draggingArea,
      {
        x: noRotateRect.x,
        y: noRotateRect.y,
      },
      noRotateRect,
      degree,
      elementType.Text
    );
    const rotateResult = rotateText(getRectPoints({ ...noRotateRect, x: realPos.x, y: realPos.y }), degree);
    let outlineRect = rotateResult.outlineRect;

    dispatch(
      resizeElement({
        imprintAreaKey,
        guid,
        x: realPos.x,
        y: realPos.y,
        width: noRotateRect.width,
        height: noRotateRect.height,
        noRotateRect: { ...noRotateRect, x: realPos.x, y: realPos.y },
        outlineRect: outlineRect,
        fontSize: newFontSize,
      })
    );
  };
};

export const getHeightByWidth = function (width, natureWidth, natureHeight) {
  return Math.floor((width * natureHeight) / natureWidth);
};
export const getWidthByHeight = function (height, natureWidth, natureHeight) {
  return Math.floor((height * natureWidth) / natureHeight);
};
export const getCmykIndex = (cmyk) => colors.findIndex((color) => color[1] === cmyk);

export const getShowColorChangeConfirmation = (oldColor, newColor) => (dispatch, getState) => {
  const state = getState();

  const studioItemType = getStudioItemType(state);

  if (studioItemType === studioItemTypeEnum.digital) {
    return { showConfirmation: false };
  } else if (studioItemType === studioItemTypeEnum.screenPrintedDigital) {
    const showColorTextChangeSetting = state.nextgenUi.showColorChangeConfirmation;
    if (!showColorTextChangeSetting) {
      return { showConfirmation: false };
    }

    const batchSelectedText = getBatchSelectedTextList(state);
    const batchMode = batchSelectedText && batchSelectedText.length > 1;

    const { elements, changeAll } = getColorChangeElements(oldColor, newColor, state);
    const willChangeTextOnly = elements.every((x) => x.data.imprintText || x.data.defaultText);

    let showConfirmation = batchMode ? elements.length > batchSelectedText.length : elements.length > 1;
    if (!batchMode && !showConfirmation && elements.length === 1 && changeAll) {
      const isImageElement = !elements[0].data.imprintText && !elements[0].data.defaultText;
      if (isImageElement) {
        const changingColors = elements[0].data.colors.filter(
          (c) => c.colorType === oldColor.colorType && c.colorId === oldColor.colorId
        );
        showConfirmation = changingColors.length > 1;
      }
    }

    return { showConfirmation, willChangeTextOnly };
  }
};
